import {useDataTable} from '@/hooks/useDataTable-hook';
import {GetFormattedSalesTargetData} from '@/models/SalesTargetListModel';
import {SalesTargetsColConfig} from '@/services/MockData/SalesTargets/SalesTargetsColConfig';
import {getSalesTargetsList} from '@/services/salesTargets';
import {useQueries} from '@tanstack/react-query';
import {buildQueryParams} from '@/utils/commons';
import {useMemo} from 'react';

const initialSortConfig = {
  column: SalesTargetsColConfig[8].name_of_db_column,
  direction: 'desc'
};

/**
 * Hook for sales targets data fetching, pagination, sorting, and search state.
 * @param {boolean} enableQuery - Whether to enable the sales targets list query.
 */
export const useSalesTargetsQuery = (enableQuery = true) => {
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);

  const queryParams = useMemo(() => {
    return buildQueryParams({
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      pageSize: 10,
      page: pageIndex || 0
    });
  }, [sortingObj?.column, sortingObj?.direction, pageIndex]);

  const isValidDateRange = useMemo(() => {
    if (!searchArr?.length) return true;
    const dateRangeSearch = searchArr.find((s) => s.type === 'dateRange');
    if (!dateRangeSearch) return true;
    const [startDate, endDate] = dateRangeSearch.value.split(',');
    return Boolean(
      startDate && endDate && !dateRangeSearch.value.endsWith(',')
    );
  }, [searchArr]);

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['SALES_TARGETS', queryParams, searchArr],
        queryFn: () => getSalesTargetsList(queryParams, searchArr),
        enabled: enableQuery && isValidDateRange
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);

  const salesTargetsData = useMemo(
    () => GetFormattedSalesTargetData(resultsData),
    [resultsData]
  );

  return {
    salesTargetsColConfigData: SalesTargetsColConfig,
    salesTargetsData,
    pageIndex,
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    searchArr,
    isLoading: isLoading || isFetching,
    handleResetAll,
    isValidDateRange,
    queryParams
  };
};
